import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * ExerciseCalendarPage - Calendar view for exercise scheduling and tracking
 * ISO 22301 Clause 8.5 - Exercising and Testing
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { computeStallStatus, CURRENCY_BADGE } from "../utils/exerciseCurrency";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Users,
  Building2,
  Bell,
  Play,
  Target,
  TrendingUp,
  CalendarDays,
  Send,
  Sparkles,
  Bot,
  Loader2,
  Zap,
  FileText,
  CalendarPlus,
  XCircle,
} from "lucide-react";

const EXERCISE_TYPES = {
  tabletop: { label: "Tabletop", color: "bg-blue-100 text-blue-700" },
  walkthrough: { label: "Walkthrough", color: "bg-purple-100 text-purple-700" },
  simulation: { label: "Simulation", color: "bg-amber-100 text-amber-700" },
  functional: { label: "Functional", color: "bg-emerald-100 text-emerald-700" },
  full_scale: { label: "Full Scale", color: "bg-red-100 text-red-700" },
};

const STATUS_COLORS = {
  planned: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
};

export default function ExerciseCalendarPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [calendarData, setCalendarData] = useState(null);
  const [departmentStats, setDepartmentStats] = useState([]);
  const [upcomingExercises, setUpcomingExercises] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  
  // AI Scheduler state
  const [showAIScheduler, setShowAIScheduler] = useState(false);
  const [aiLoading, setAILoading] = useState(false);
  const [aiSchedule, setAISchedule] = useState(null);
  const [recoveryPackages, setRecoveryPackages] = useState([]);

  // v2 — Asset-owner scheduling + proposals
  const MANAGER_ROLES = ["admin", "bcm_manager", "department_head", "it_drp_manager",
    "process_manager", "it_function_manager", "app_manager"];
  const CONTRIBUTOR_ROLES = ["process_contributor", "it_function_contributor", "app_contributor"];
  const canSchedule = MANAGER_ROLES.includes(user?.role) || CONTRIBUTOR_ROLES.includes(user?.role);
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: "", description: "", exercise_type: "tabletop",
    scheduled_date: "", duration_hours: 2, participants: "",
    asset_name: "", asset_type: "process", rto_hours: 4,
  });
  const [aiSlots, setAiSlots] = useState(null);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiSlotLoading, setAiSlotLoading] = useState(false);
  const [conflictPreview, setConflictPreview] = useState(null);
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [exerciseFreqMonths, setExerciseFreqMonths] = useState(12);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/organizations/current");
        const org = res.data || {};
        setExerciseFreqMonths(org.exercise_frequency_months || org.exercise_frequency_recommended || 12);
      } catch (e) { /* ignore */ }
    })();
  }, [api]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      // Source calendar events from the single de-duplicated union endpoint.
      // /exercise-calendar/events already returns each exercise exactly once,
      // so we no longer also fetch /recovery-packages/calendar/exercises and
      // merge (that dual-fetch was the source of duplicate/inflated counts).
      // Spread the raw event so any fields the endpoint provides — including
      // linked_packages / package_id — are preserved intact for the detail modal.
      let baseData = { events: [], total_exercises: 0 };
      try {
        const v2Res = await api.get("/exercise-calendar/events");
        const events = (v2Res.data?.events || []).map((e) => ({
          ...e,
          source: e.source || "calendar_v2",
        }));
        baseData = { ...(v2Res.data || {}), events, total_exercises: events.length };
      } catch (err) {
        console.log("calendar events fetch failed:", err?.message);
        baseData = { events: [], total_exercises: 0 };
      }
      setCalendarData(baseData);

      // Proposals (managers only)
      if (isManager) {
        try {
          const propRes = await api.get("/exercise-calendar/proposals");
          setProposals(propRes.data?.proposals || []);
        } catch (err) { /* ignore */ }
      }

      // Fetch department stats (BCM Manager only)
      if (isBCMAdmin) {
        const statsResponse = await api.get("/recovery-packages/calendar/department-stats");
        setDepartmentStats(statsResponse.data);
        
        // Fetch recovery packages for AI scheduler
        try {
          const packagesResponse = await api.get("/recovery-packages/approved");
          setRecoveryPackages(packagesResponse.data || []);
        } catch (err) {
          console.log("Could not fetch recovery packages for AI scheduler");
        }
      }

      // Fetch upcoming exercises
      const upcomingResponse = await api.get("/recovery-packages/calendar/upcoming?days=30");
      setUpcomingExercises(upcomingResponse.data);
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  }, [api, isBCMAdmin, isManager]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await api.post("/recovery-packages/calendar/send-reminders");
      toast.success(`Sent ${response.data.reminders_sent} exercise reminders`);
    } catch (error) {
      console.error("Failed to send reminders:", error);
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  // AI Exercise Scheduler
  const handleAISchedule = async () => {
    setAILoading(true);
    setAISchedule(null);
    
    try {
      // Prepare packages data for AI
      const packagesForAI = recoveryPackages.map(pkg => ({
        id: pkg._id || pkg.id,
        name: pkg.process_name || pkg.name,
        rto_hours: pkg.target_rto || pkg.rto_hours || 4,
        criticality: pkg.criticality_tier || "Tier 2",
        department: pkg.department || "General",
        last_tested: pkg.last_test_date || null
      }));

      // Get existing exercises
      const existingExercises = calendarData?.events?.map(e => ({
        id: e.id,
        name: e.title,
        scheduled_date: e.scheduled_date,
        exercise_type: e.exercise_type,
        package_id: e.package_id
      })) || [];

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai-assistant/schedule-exercises`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packages: packagesForAI,
          existing_exercises: existingExercises,
          preferences: {
            avoid_weekends: true,
            max_exercises_per_month: 4,
            prefer_quarterly_testing: true
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setAISchedule(data.data);
        toast.success("AI schedule generated successfully!");
      } else {
        throw new Error("Failed to generate schedule");
      }
    } catch (error) {
      console.error("AI scheduling failed:", error);
      toast.error("Failed to generate AI schedule. Please try again.");
    } finally {
      setAILoading(false);
    }
  };

  const getMonthDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  // ─── v2 Asset-owner scheduling handlers ───
  const participantsArr = () =>
    (scheduleForm.participants || "")
      .split(",").map((p) => p.trim()).filter(Boolean);

  const handleAISuggestSlots = async () => {
    setAiSlotLoading(true);
    setAiSlots(null);
    setAiDraft(null);
    try {
      const res = await api.post("/exercise-calendar/ai-suggest-slots", {
        exercise_type: scheduleForm.exercise_type,
        asset_name: scheduleForm.asset_name || scheduleForm.name,
        asset_type: scheduleForm.asset_type,
        rto_hours: Number(scheduleForm.rto_hours) || 4,
        duration_hours: Number(scheduleForm.duration_hours) || 2,
        participants: participantsArr(),
      });
      setAiSlots(res.data?.slots || []);
      setAiDraft(res.data?.draft || null);
      if (res.data?.best_slot) {
        setScheduleForm((f) => ({ ...f, scheduled_date: res.data.best_slot }));
      }
      toast.success("AI suggested conflict-free slots");
    } catch (err) {
      toast.error(extractErrorMessage(err, "AI scheduling failed"));
    } finally {
      setAiSlotLoading(false);
    }
  };

  const handleCheckConflicts = async () => {
    if (!scheduleForm.scheduled_date) {
      toast.error("Pick a date first");
      return;
    }
    try {
      const res = await api.post("/exercise-calendar/check-conflicts", {
        scheduled_date: scheduleForm.scheduled_date,
        duration_hours: Number(scheduleForm.duration_hours) || 2,
        participants: participantsArr(),
      });
      setConflictPreview(res.data);
      if (!res.data.has_conflict) toast.success("No conflicts detected");
      else toast.warning(`${res.data.conflicts.length} conflict(s) found`);
    } catch (err) {
      toast.error("Conflict check failed");
    }
  };

  const handleSubmitSchedule = async () => {
    if (!scheduleForm.name || !scheduleForm.scheduled_date) {
      toast.error("Name and date are required");
      return;
    }
    setScheduleSubmitting(true);
    const payload = {
      name: scheduleForm.name,
      description: scheduleForm.description,
      exercise_type: scheduleForm.exercise_type,
      scheduled_date: scheduleForm.scheduled_date,
      duration_hours: Number(scheduleForm.duration_hours) || 2,
      participants: participantsArr(),
      asset_ref_type: scheduleForm.asset_type,
      scope: aiDraft?.scope || "",
      objectives: aiDraft?.objectives || [],
      scenario_description: aiDraft?.scenario_description || "",
      success_criteria: aiDraft?.success_criteria || [],
    };
    try {
      const endpoint = isManager
        ? "/exercise-calendar/schedule"
        : "/exercise-calendar/propose";
      await api.post(endpoint, payload);
      toast.success(
        isManager ? "Exercise scheduled" : "Proposal submitted — awaiting manager approval"
      );
      setShowScheduleDialog(false);
      setScheduleForm({
        name: "", description: "", exercise_type: "tabletop",
        scheduled_date: "", duration_hours: 2, participants: "",
        asset_name: "", asset_type: "process", rto_hours: 4,
      });
      setAiSlots(null); setAiDraft(null); setConflictPreview(null);
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to schedule"));
    } finally {
      setScheduleSubmitting(false);
    }
  };

  const handleProposalDecision = async (proposalId, decision) => {
    try {
      await api.post(
        `/exercise-calendar/proposals/${proposalId}/${decision}`,
        { reason: decision === "reject" ? "Rejected by manager" : "" }
      );
      toast.success(`Proposal ${decision}d`);
      fetchData();
    } catch (err) {
      toast.error(`Failed to ${decision} proposal`);
    }
  };

  // Local-time date key (YYYY-MM-DD from the viewer's local calendar). Used by
  // both the grid cell matcher below and — implicitly — the detail modal, which
  // renders scheduled_date via new Date(...).toLocaleDateString(). Interpreting
  // the stored timestamp in LOCAL time in both places keeps an exercise on the
  // same day in the grid and the modal regardless of the viewer's US timezone.
  const toLocalYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const getEventsForDate = (date) => {
    if (!calendarData?.events || !date) return [];

    const cellStr = toLocalYMD(date);
    return calendarData.events.filter((event) => {
      if (!event.scheduled_date) return false;
      const ed = new Date(event.scheduled_date);
      if (isNaN(ed.getTime())) return false;
      return toLocalYMD(ed) === cellStr;
    });
  };

  const openEventDetails = (event) => {
    setSelectedEvent(event);
    setShowEventDialog(true);
  };

  // Per-marker exercise testing currency for the calendar.
  // Completed exercises use P31d completion recency (compute_stall_status, unchanged).
  // Planned/not-yet-completed exercises are graded against their scheduled date:
  //   scheduled date passed & never conducted -> overdue (red)
  //   scheduled within 30 days               -> approaching_due (amber)
  //   scheduled >30 days out                 -> current (green)
  //   no scheduled date & never completed    -> never_tested (gray)
  const getEventCurrency = (event) => {
    if (event?.status === "completed" && event?.completed_date) {
      return computeStallStatus(event.completed_date, exerciseFreqMonths);
    }
    const sched = event?.scheduled_date ? new Date(event.scheduled_date) : null;
    if (!sched || isNaN(sched.getTime())) {
      return { effective_status: "never_tested" };
    }
    const daysTo = Math.round((sched.getTime() - Date.now()) / 86400000);
    if (daysTo < 0) return { effective_status: "overdue", days_overdue: -daysTo, deadline: sched.toISOString() };
    if (daysTo <= 30) return { effective_status: "approaching_due", days_remaining: daysTo, deadline: sched.toISOString() };
    return { effective_status: "current", days_remaining: daysTo, deadline: sched.toISOString() };
  };

  const CURRENCY_HEX = {
    current: "#10b981",
    approaching_due: "#f59e0b",
    overdue: "#ef4444",
    never_tested: "#94a3b8",
  };

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  const monthDays = getMonthDays(currentMonth);

  return (
    <div className="space-y-6" data-testid="exercise-calendar-page">
      {/* Overdue currency banner (Surface 2 / Defect 3) */}
      {(() => {
        const events = calendarData?.events || [];
        const completed = events.filter(e => e.status === "completed" && e.completed_date);
        const lastCompleted = completed.length
          ? completed.map(e => e.completed_date).sort().slice(-1)[0]
          : null;
        // Org-level testing currency: never_tested when nothing has been
        // completed, otherwise based on the most recent completion.
        const orgStatus = computeStallStatus(lastCompleted, exerciseFreqMonths).effective_status;
        if (orgStatus !== "overdue" && orgStatus !== "never_tested") return null;
        if (events.length === 0) return null;
        const label = orgStatus === "never_tested"
          ? "No exercises completed — the organization is overdue for exercise testing. Schedule and conduct an exercise to establish program currency."
          : "Exercise testing is overdue — schedule and conduct a new exercise to restore program currency.";
        return (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700" data-testid="calendar-overdue-banner">
            <AlertTriangle className="w-4 h-4" /> {label}
          </div>
        );
      })()}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 8.5</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Exercise Calendar</h1>
          <p className="text-slate-600 mt-1">
            Schedule, track, and manage exercise testing activities
          </p>
        </div>
        <div className="flex gap-2">
          {canSchedule && (
            <Button
              onClick={() => setShowScheduleDialog(true)}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
              data-testid="schedule-exercise-btn"
            >
              <CalendarPlus className="w-4 h-4 mr-2" />
              {isManager ? "Schedule Exercise" : "Propose Exercise"}
            </Button>
          )}
          {isBCMAdmin && (
            <>
              <Button
                onClick={() => setShowAIScheduler(true)}
                className="bg-gradient-to-r from-sky-700 to-teal-600 hover:from-indigo-700 hover:to-purple-700"
                data-testid="ai-scheduler-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Scheduler
              </Button>
              <Button
                variant="outline"
                onClick={handleSendReminders}
                disabled={sendingReminders}
              >
                {sendingReminders ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Send Reminders
              </Button>
            </>
          )}
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <CalendarDays className="w-4 h-4 text-slate-400" />
              <p className="text-sm text-slate-500">Total Exercises</p>
            </div>
            <p className="text-2xl font-bold text-slate-900">
              {calendarData?.total_exercises || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-blue-500" />
              <p className="text-sm text-slate-500">Upcoming (30 days)</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {upcomingExercises.length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Play className="w-4 h-4 text-amber-500" />
              <p className="text-sm text-slate-500">In Progress</p>
            </div>
            <p className="text-2xl font-bold text-amber-600">
              {calendarData?.events?.filter((e) => e.status === "in_progress").length || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <p className="text-sm text-slate-500">Completed</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">
              {calendarData?.events?.filter((e) => e.status === "completed").length || 0}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </CardTitle>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} data-testid="calendar-prev-month-btn">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentMonth(new Date())}
                  data-testid="calendar-today-btn"
                >
                  Today
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} data-testid="calendar-next-month-btn">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Testing-currency legend (P32d prominence) */}
            <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-slate-500" data-testid="calendar-currency-legend">
              <span className="font-medium text-slate-600">Testing currency:</span>
              {["current", "approaching_due", "overdue", "never_tested"].map((k) => (
                <span key={k} className="inline-flex items-center gap-1">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white shadow-sm"
                    style={{ backgroundColor: CURRENCY_HEX[k], boxShadow: `0 0 0 1px ${CURRENCY_HEX[k]}` }}
                    data-currency={k}
                  />
                  {CURRENCY_BADGE[k].label}
                </span>
              ))}
            </div>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-slate-500 py-2"
                >
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {monthDays.map((date, idx) => {
                if (!date) {
                  return <div key={`empty-${idx}`} className="h-24" />;
                }

                const events = getEventsForDate(date);
                const isToday = date.toDateString() === new Date().toDateString();

                return (
                  <div
                    key={date.toISOString()}
                    className={`h-24 border rounded-lg p-1 ${
                      isToday ? "border-sky-700 bg-sky-50" : "border-slate-200"
                    }`}
                  >
                    <div
                      className={`text-xs font-medium mb-1 ${
                        isToday ? "text-sky-700" : "text-slate-600"
                      }`}
                    >
                      {date.getDate()}
                    </div>
                    <div className="space-y-1 overflow-y-auto max-h-16">
                      {events.slice(0, 2).map((event) => {
                        const _stall = getEventCurrency(event);
                        const _cur = _stall.effective_status;
                        const _badge = CURRENCY_BADGE[_cur] || CURRENCY_BADGE.never_tested;
                        const _color = CURRENCY_HEX[_cur] || CURRENCY_HEX.never_tested;
                        const _tip = _cur === "overdue"
                          ? `${event.title} — Overdue by ${_stall.days_overdue} day(s)${_stall.deadline ? ` (was due ${new Date(_stall.deadline).toLocaleDateString()})` : ""}.`
                          : _cur === "approaching_due"
                          ? `${event.title} — Approaching Due in ${_stall.days_remaining} day(s).`
                          : _cur === "current"
                          ? `${event.title} — Current (${_stall.days_remaining} day(s) until scheduled).`
                          : `${event.title} — Never Tested (no schedule or completion on record).`;
                        return (
                        <div
                          key={event.id}
                          className={`text-[10px] px-1 py-0.5 rounded cursor-pointer truncate flex items-center gap-1 border-l-4 ${
                            EXERCISE_TYPES[event.exercise_type]?.color || "bg-slate-100"
                          }`}
                          style={{ borderLeftColor: _color }}
                          onClick={() => openEventDetails(event)}
                          title={_tip}
                          data-testid={`calendar-event-${event.id}`}
                        >
                          <span
                            className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ring-2 ring-white shadow-sm ${_cur === "overdue" ? "cal-currency-pulse" : ""}`}
                            style={{ backgroundColor: _color, boxShadow: `0 0 0 1px ${_color}` }}
                            title={`${event.title} — ${_badge.label}`}
                            data-testid={`calendar-currency-dot-${event.id}`}
                            data-currency={_cur}
                          />
                          {event.title}
                        </div>
                        );
                      })}
                      {events.length > 2 && (
                        <div className="text-[10px] text-slate-500 pl-1">
                          +{events.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Upcoming Exercises */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Upcoming Exercises
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[300px] overflow-y-auto">
                {upcomingExercises.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    No upcoming exercises
                  </div>
                ) : (
                  upcomingExercises.map((exercise) => (
                    <div
                      key={exercise.id}
                      className="p-3 hover:bg-slate-50 cursor-pointer"
                      onClick={() => {
                        const event = calendarData?.events?.find(
                          (e) => e.id === exercise.id
                        );
                        if (event) openEventDetails(event);
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-sm">{exercise.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge
                              className={`text-[10px] ${
                                EXERCISE_TYPES[exercise.exercise_type]?.color
                              }`}
                            >
                              {EXERCISE_TYPES[exercise.exercise_type]?.label}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {exercise.duration_hours}h
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          {exercise.days_until !== null && (
                            <Badge
                              variant="outline"
                              className={
                                exercise.days_until <= 3
                                  ? "border-red-300 text-red-700"
                                  : exercise.days_until <= 7
                                  ? "border-amber-300 text-amber-700"
                                  : ""
                              }
                            >
                              {exercise.days_until === 0
                                ? "Today"
                                : exercise.days_until === 1
                                ? "Tomorrow"
                                : `${exercise.days_until} days`}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Department Stats (BCM Manager only) */}
          {isBCMAdmin && departmentStats.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Department Completion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {departmentStats.map((dept) => (
                  <div key={dept.department}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">{dept.department}</span>
                      <span className="text-sm text-slate-500">
                        {dept.completion_rate}%
                      </span>
                    </div>
                    <Progress
                      value={dept.completion_rate}
                      className={`h-2 ${
                        dept.completion_rate >= 80
                          ? "[&>div]:bg-emerald-500"
                          : dept.completion_rate >= 50
                          ? "[&>div]:bg-amber-500"
                          : "[&>div]:bg-red-500"
                      }`}
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-0.5">
                      <span>
                        {dept.packages_with_approved_tests}/{dept.total_packages} tested
                      </span>
                      <span>
                        {dept.passed_tests} passed, {dept.pending_tests} pending
                      </span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Legend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Exercise Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(EXERCISE_TYPES).map(([key, config]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded ${config.color}`} />
                  <span className="text-sm text-slate-600">{config.label}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pending Proposals (Manager only) */}
      {isManager && proposals.length > 0 && (
        <Card data-testid="proposals-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Pending Exercise Proposals ({proposals.length})
            </CardTitle>
            <CardDescription>
              Contributors have proposed these exercises. Approve to add to the calendar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {proposals.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`proposal-${p.id}`}>
                <div>
                  <p className="font-medium text-sm">{p.name}</p>
                  <p className="text-xs text-slate-500">
                    {p.exercise_type} • {new Date(p.scheduled_date).toLocaleDateString()} •
                    {" "}{p.duration_hours}h • by {p.created_by_role || "contributor"}
                    {p.conflicts_at_creation?.length > 0 && (
                      <span className="text-red-600 ml-2">⚠ {p.conflicts_at_creation.length} conflict(s)</span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => handleProposalDecision(p.id, "reject")}
                    data-testid={`reject-proposal-${p.id}`}>
                    <XCircle className="w-4 h-4 mr-1" /> Reject
                  </Button>
                  <Button size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleProposalDecision(p.id, "approve")}
                    data-testid={`approve-proposal-${p.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Event Details Dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedEvent?.title}</DialogTitle>
            <DialogDescription>
              <Badge className={EXERCISE_TYPES[selectedEvent?.exercise_type]?.color}>
                {EXERCISE_TYPES[selectedEvent?.exercise_type]?.label}
              </Badge>
              <Badge className={`ml-2 ${STATUS_COLORS[selectedEvent?.status]}`}>
                {selectedEvent?.status?.replace("_", " ")}
              </Badge>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Scheduled Date</p>
                <p className="font-medium">
                  {selectedEvent?.scheduled_date
                    ? new Date(selectedEvent.scheduled_date).toLocaleDateString()
                    : "TBD"}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Duration</p>
                <p className="font-medium">{selectedEvent?.duration_hours} hours</p>
              </div>
            </div>

            {selectedEvent?.participants?.length > 0 && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Participants</p>
                <div className="flex flex-wrap gap-1">
                  {selectedEvent.participants.map((p, idx) => (
                    <Badge key={idx} variant="outline">
                      {p}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {selectedEvent?.linked_packages?.length > 0 && (
              <div>
                <p className="text-sm text-slate-500 mb-2">Linked Recovery Packages</p>
                <div className="space-y-2">
                  {selectedEvent.linked_packages.map((pkg, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded"
                    >
                      <div>
                        <p className="font-medium text-sm">{pkg.package_name}</p>
                        <p className="text-xs text-slate-500">{pkg.department}</p>
                      </div>
                      <Badge
                        className={
                          pkg.test_status === "approved"
                            ? "bg-emerald-100 text-emerald-700"
                            : pkg.test_status === "submitted"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-slate-100 text-slate-700"
                        }
                      >
                        {pkg.test_status?.replace("_", " ") || "Not Started"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEventDialog(false)}>
              Close
            </Button>
            <Button onClick={() => navigate("/exercises")}>
              <Play className="w-4 h-4 mr-2" />
              Go to Exercises
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Scheduler Dialog */}
      <Dialog open={showAIScheduler} onOpenChange={setShowAIScheduler}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <span>AI Exercise Scheduler</span>
                <p className="text-sm font-normal text-slate-500 mt-0.5">
                  Generate optimal exercise schedule based on risk and RTO
                </p>
              </div>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {!aiSchedule && !aiLoading && (
              <div className="text-center py-8">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-sky-700" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  AI-Powered Exercise Planning
                </h3>
                <p className="text-slate-600 max-w-md mx-auto mb-6">
                  Our AI analyzes your recovery packages, criticality tiers, and RTO requirements
                  to create an optimal testing schedule for ISO 22301 compliance.
                </p>
                <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto mb-6 text-left">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Target className="w-5 h-5 text-sky-700 mb-2" />
                    <p className="text-xs font-medium text-slate-700">Risk-Based</p>
                    <p className="text-[10px] text-slate-500">Prioritizes critical packages</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <Clock className="w-5 h-5 text-purple-600 mb-2" />
                    <p className="text-xs font-medium text-slate-700">RTO-Aware</p>
                    <p className="text-[10px] text-slate-500">Matches testing to RTO</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-emerald-600 mb-2" />
                    <p className="text-xs font-medium text-slate-700">Progressive</p>
                    <p className="text-[10px] text-slate-500">Tabletop → Full-scale</p>
                  </div>
                </div>
                <Button
                  onClick={handleAISchedule}
                  className="bg-gradient-to-r from-sky-700 to-teal-600 hover:from-indigo-700 hover:to-purple-700"
                  disabled={recoveryPackages.length === 0}
                >
                  <Zap className="w-4 h-4 mr-2" />
                  Generate AI Schedule
                </Button>
                {recoveryPackages.length === 0 && (
                  <p className="text-xs text-amber-600 mt-2">
                    No approved recovery packages found. Create packages first.
                  </p>
                )}
              </div>
            )}

            {aiLoading && (
              <div className="text-center py-12">
                <Loader2 className="w-12 h-12 animate-spin text-sky-700 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Analyzing packages and generating schedule...</p>
                <p className="text-sm text-slate-400 mt-1">This may take 30-60 seconds</p>
              </div>
            )}

            {aiSchedule && (
              <div className="space-y-6">
                {/* Compliance Impact */}
                {aiSchedule.compliance_impact && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-semibold text-emerald-800">Compliance Impact</h4>
                    </div>
                    <p className="text-sm text-emerald-700">{aiSchedule.compliance_impact}</p>
                  </div>
                )}

                {/* Schedule Table */}
                {aiSchedule.schedule && aiSchedule.schedule.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <CalendarPlus className="w-4 h-4" />
                      Recommended Exercise Schedule
                    </h4>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Package</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Type</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Date</th>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Priority</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {aiSchedule.schedule.map((item, idx) => (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-3 py-2">
                                <p className="font-medium text-slate-900">{item.package_name}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{item.rationale}</p>
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={
                                  item.exercise_type === "Tabletop" ? "bg-blue-100 text-blue-700" :
                                  item.exercise_type === "Walkthrough" ? "bg-purple-100 text-purple-700" :
                                  item.exercise_type === "Simulation" ? "bg-amber-100 text-amber-700" :
                                  item.exercise_type === "Functional" ? "bg-emerald-100 text-emerald-700" :
                                  "bg-red-100 text-red-700"
                                }>
                                  {item.exercise_type}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-slate-600">
                                {item.recommended_date}
                              </td>
                              <td className="px-3 py-2">
                                <Badge className={
                                  item.priority === "High" ? "bg-red-100 text-red-700" :
                                  item.priority === "Medium" ? "bg-amber-100 text-amber-700" :
                                  "bg-slate-100 text-slate-700"
                                }>
                                  {item.priority}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {aiSchedule.recommendations && aiSchedule.recommendations.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      AI Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {aiSchedule.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowAIScheduler(false)}>
              Close
            </Button>
            {aiSchedule && (
              <Button
                onClick={() => {
                  toast.success("Schedule exported! Check your exercises page to create these exercises.");
                  setShowAIScheduler(false);
                }}
                className="bg-sky-700 hover:bg-sky-800"
              >
                <CalendarPlus className="w-4 h-4 mr-2" />
                Apply Schedule
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Asset-Owner Schedule Exercise Dialog (v2) */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="schedule-exercise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="w-5 h-5 text-emerald-600" />
              {isManager ? "Schedule New Exercise" : "Propose New Exercise"}
            </DialogTitle>
            <DialogDescription>
              {isManager
                ? "Schedule an exercise for your asset with AI conflict detection."
                : "Submit a proposal — your line manager will review and approve."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Exercise Name *</Label>
                <Input
                  data-testid="ex-name"
                  value={scheduleForm.name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                  placeholder="Q2 Payment System Tabletop"
                />
              </div>
              <div>
                <Label>Exercise Type *</Label>
                <Select
                  value={scheduleForm.exercise_type}
                  onValueChange={(v) => setScheduleForm({ ...scheduleForm, exercise_type: v })}
                >
                  <SelectTrigger data-testid="ex-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tabletop">Tabletop</SelectItem>
                    <SelectItem value="walkthrough">Walkthrough</SelectItem>
                    <SelectItem value="simulation">Simulation</SelectItem>
                    <SelectItem value="functional">Functional</SelectItem>
                    <SelectItem value="full_scale">Full Scale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Asset Name</Label>
                <Input
                  data-testid="ex-asset"
                  value={scheduleForm.asset_name}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, asset_name: e.target.value })}
                  placeholder="Core Payments Platform"
                />
              </div>
              <div>
                <Label>Asset Type</Label>
                <Select
                  value={scheduleForm.asset_type}
                  onValueChange={(v) => setScheduleForm({ ...scheduleForm, asset_type: v })}
                >
                  <SelectTrigger data-testid="ex-asset-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="process">Process</SelectItem>
                    <SelectItem value="application">Application</SelectItem>
                    <SelectItem value="it_function">IT Function</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Scheduled Date/Time *</Label>
                <Input
                  type="datetime-local"
                  data-testid="ex-date"
                  value={scheduleForm.scheduled_date ? scheduleForm.scheduled_date.slice(0, 16) : ""}
                  onChange={(e) =>
                    setScheduleForm({ ...scheduleForm, scheduled_date: e.target.value + ":00Z" })
                  }
                />
              </div>
              <div>
                <Label>Duration (hours)</Label>
                <Input
                  type="number" min="0.5" step="0.5"
                  data-testid="ex-duration"
                  value={scheduleForm.duration_hours}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, duration_hours: e.target.value })}
                />
              </div>
              <div>
                <Label>RTO (hours) — for AI context</Label>
                <Input
                  type="number" min="0.5" step="0.5"
                  data-testid="ex-rto"
                  value={scheduleForm.rto_hours}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, rto_hours: e.target.value })}
                />
              </div>
              <div>
                <Label>Participants (comma-separated emails/names)</Label>
                <Input
                  data-testid="ex-participants"
                  value={scheduleForm.participants}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, participants: e.target.value })}
                  placeholder="alice@co.com, bob@co.com"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                rows={2} data-testid="ex-desc"
                value={scheduleForm.description}
                onChange={(e) => setScheduleForm({ ...scheduleForm, description: e.target.value })}
              />
            </div>

            {/* AI Panel */}
            <div className="bg-gradient-to-r from-sky-50 to-teal-50 border border-sky-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-sky-700" />
                  <h4 className="font-semibold text-sky-900">AI Assistant</h4>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={handleCheckConflicts}
                    data-testid="check-conflicts-btn">
                    Check Conflicts
                  </Button>
                  <Button size="sm"
                    onClick={handleAISuggestSlots}
                    disabled={aiSlotLoading}
                    className="bg-sky-700 hover:bg-sky-800 text-white"
                    data-testid="ai-suggest-slots-btn">
                    {aiSlotLoading ? (
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-1" />
                    )}
                    Suggest Optimal Slots
                  </Button>
                </div>
              </div>

              {conflictPreview && (
                <div
                  className={`mb-3 p-2 rounded text-sm ${
                    conflictPreview.has_conflict
                      ? "bg-red-50 border border-red-200 text-red-700"
                      : "bg-emerald-50 border border-emerald-200 text-emerald-700"
                  }`}
                  data-testid="conflict-result">
                  {conflictPreview.has_conflict ? (
                    <>⚠ {conflictPreview.conflicts.length} conflict(s) detected:
                      <ul className="list-disc pl-5 mt-1">
                        {conflictPreview.conflicts.slice(0, 3).map((c, i) => (
                          <li key={i}>{c.title} ({c.type})</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <>✓ No conflicts for this slot.</>
                  )}
                </div>
              )}

              {aiSlots && aiSlots.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs font-semibold text-slate-600 mb-1">
                    AI-recommended conflict-free slots:
                  </p>
                  <div className="space-y-1">
                    {aiSlots.map((slot, i) => (
                      <button
                        key={i}
                        onClick={() => setScheduleForm({ ...scheduleForm, scheduled_date: slot.start })}
                        className={`w-full text-left text-sm p-2 rounded border hover:bg-white transition ${
                          scheduleForm.scheduled_date === slot.start
                            ? "border-sky-500 bg-white"
                            : "border-slate-200"
                        }`}
                        data-testid={`ai-slot-${i}`}
                      >
                        <div className="font-medium">
                          {new Date(slot.start).toLocaleString()}
                        </div>
                        <div className="text-xs text-slate-500">{slot.reason}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {aiDraft && (
                <div className="mt-3 text-sm bg-white/80 rounded p-3 border" data-testid="ai-draft">
                  <p className="font-semibold text-slate-700 mb-1">AI-drafted scope:</p>
                  <p className="text-slate-600 mb-2">{aiDraft.scope}</p>
                  {aiDraft.objectives?.length > 0 && (
                    <>
                      <p className="font-semibold text-slate-700 text-xs mb-1">Objectives:</p>
                      <ul className="list-disc pl-5 text-xs text-slate-600">
                        {aiDraft.objectives.map((o, i) => <li key={i}>{o}</li>)}
                      </ul>
                    </>
                  )}
                  <p className="text-[10px] text-slate-400 mt-2">
                    Source: {aiDraft._source}
                  </p>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitSchedule}
              disabled={scheduleSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-schedule-btn">
              {scheduleSubmitting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CalendarPlus className="w-4 h-4 mr-2" />
              )}
              {isManager ? "Schedule" : "Submit Proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
